import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Button,
} from '@mui/material';
import { useApiGet } from '../../utils/httpClient';
import { getTLCList } from '../../endpoints/resources';
import TablePeo from '../../Components/TablePeo';
import SPDSelector from '../../Components/SPDSelector';

const headCells = [
  {
    id: 'name',
    firstCell: true,
    label: 'Name',
    width: '50%',
  },
  {
    id: 'state',
    label: 'State',
    width: '30%',
  },
  {
    id: 'is_active',
    label: 'Active?',
    width: '20%',
    boolean: true,
  },
];

export default function TLCList() {
  let [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key, value) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showMore, setShowMore] = useState(false);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [delaySearch, setDelaySearch] = useState(
    searchParams.get('search') || ''
  );
  const [spdSelected, setSpdSelected] = useState(searchParams.get('spd') || '');

  // need up update search params when search changes
  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  // need up update search params when spdSelected changes
  useEffect(() => {
    updateSearchParams('spd', spdSelected);
  }, [spdSelected, updateSearchParams]);

  const { data, isFetching } = useApiGet(
    ['tlc-list', rowsPerPage, page, orderBy, order, search, spdSelected],
    getTLCList,
    {
      enabled: true,
      retry: 1,
    },
    {
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      spd: spdSelected,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handleClick = (event, id) => {
    window.location.href = `/tlc/${id}`;
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding textAlign={'center'}>
        <Typography variant="h4" color={'primary'}>
          TLCs
        </Typography>
      </Grid>
      <Grid item xs={12} paddingBottom={4}>
        <Typography variant="body1">
          TLC, P.E.O.’s “Tender Loving Care” program, offers a unique
          opportunity to be of service to or be assisted by P.E.O. sisters.
          <Button
            variant="text"
            color="primary"
            onClick={() => setShowMore(!showMore)}
            sx={{ textTransform: 'none' }}
          >
            {showMore ? 'Show Less' : 'Show More'}
          </Button>
        </Typography>
        {showMore && (
          <React.Fragment>
            <Typography variant="body1" padding sx={{ fontStyle: 'italic' }}>
              This program began in 1936 with Chapter BM in Rochester,
              Minnesota, home of the Mayo Clinic. The chapter wanted to assist
              P.E.O families visiting Mayo. Since then the program has expanded
              and more sisters across the United States and Canada have made
              themselves available as TLC contacts. It is a resource for P.E.O.
              members in emergency situations. Starting in 1996, TLC also
              includes support for college students who may need assistance
              while away from home.
            </Typography>
            <Typography variant="body1" padding sx={{ fontStyle: 'italic' }}>
              If you have questions, or your chapter would like to be included
              in these listings, please contact the P.E.O. Executive Office at
              commassist@peodsm.org.
            </Typography>
            <Typography variant="body1" padding sx={{ fontStyle: 'italic' }}>
              The following is a list of P.E.O.s who can help members or their
              families. P.E.O. members or their families are welcome to contact
              these individuals directly when they are in need of assistance.
            </Typography>
          </React.Fragment>
        )}
      </Grid>
      <Grid container>
        <Grid item xs={6} paddingRight={2} paddingBottom={3}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={delaySearch}
            fullWidth={true}
            onChange={(event) => setDelaySearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} paddingBottom={3}>
          <SPDSelector
            spd={spdSelected}
            handleSpdSelect={(setSpd) => {
              setSpdSelected(setSpd);
            }}
            reportId="tlc"
            all={true}
          />
        </Grid>
      </Grid>
      <TablePeo
        data={data}
        isFetching={isFetching}
        headCells={headCells}
        handleRowClick={handleClick}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
      />
    </Grid>
  );
}
